import {AxiosResponse} from 'axios';
import apiClient from '.';
import {
  AddCustodianRequest,
  AddCustodianResponse,
  AssignCustodianPayload,
  CustodianListResponse,
  DeleteFileType,
  InitiateProcessingPayload,
  InitiateProcessingResponse,
  InitiateUploadPayload,
  ProcessingSettingsResponse,
  UploadFileSessionIdPayload,
  UploadSessionResponse,
} from '../types/upload-new-eviidence/evidenceSourceTypes';

const version = 'v1';
const evidenceSourceEndpoint = (path: string) =>
  `${version}/evidencesourceupload${path}`;

export const downloadTemplateManifest = (
  clientId: string,
  projectId: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.get<Blob>(
    evidenceSourceEndpoint(
      `/manifest-template?clientId=${clientId}&projectId=${projectId}`
    ),
    {
      responseType: 'blob',
    }
  );
};

export const uploadEvidenceSource = (
  type: 'manifest' | 'passkey',
  clientId: string,
  projectId: string,
  file: File,
  evidenceSourceSessionId: string
) => {
  const formData = new FormData();
  formData.append('file', file);

  return apiClient.post<Blob>(
    evidenceSourceEndpoint(
      `/multicustodians/${type}?evidenceSourceSessionId=${evidenceSourceSessionId}&clientId=${clientId}&projectId=${projectId}`
    ),
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

export const custodianList = (
  clientId: string,
  projectId: string
): Promise<AxiosResponse<CustodianListResponse>> => {
  return apiClient.get(
    evidenceSourceEndpoint(
      `/custodians?clientId=${clientId}&projectId=${projectId}`
    )
  );
};

export const addCustodian = (
  clientId: string,
  projectId: string,
  payload: AddCustodianRequest
): Promise<AxiosResponse<AddCustodianResponse>> => {
  return apiClient.post(
    evidenceSourceEndpoint(
      `/custodians?clientId=${clientId}&projectId=${projectId}`
    ),
    payload
  );
};

export const exportEvidenceReport = (
  params?: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.get<Blob>(
    evidenceSourceEndpoint(`/staging/export?` + params),
    {
      responseType: 'blob',
    }
  );
};

export const deleteEvidenceSource = (
  type: DeleteFileType,
  clientId: string,
  projectId: string,
  fileId: string
) => {
  return apiClient.delete(
    evidenceSourceEndpoint(
      `/multicustodians/${type}/${fileId}?clientId=${clientId}&projectId=${projectId}`
    )
  );
};

export const getEvidenceSourceStaging = (
  params: string,
  payload: any
): Promise<AxiosResponse> => {
  return apiClient.post(evidenceSourceEndpoint(`/staging?` + params), payload);
};

export const createUploadFileSessionId = async (
  clientId: string,
  projectId: string,
  payload: UploadFileSessionIdPayload,
  evidenceSourceSessionId: string,
  signal?: AbortSignal
) => {
  const result = await apiClient.post(
    evidenceSourceEndpoint(
      `/https/uploads?evidenceSourceSessionId=${evidenceSourceSessionId}&clientId=${clientId}&projectId=${projectId}`
    ),
    payload,
    {signal: signal}
  );
  return result.data;
};

export const createFCUploadFileSessionId = async (
  clientId: string,
  projectId: string,
  evidenceSourceSessionId: string,
  payload: UploadFileSessionIdPayload,
  signal?: AbortSignal
) => {
  const result = await apiClient.post(
    evidenceSourceEndpoint(`/filecatalyst/uploads`),
    payload,
    {signal: signal, params: {clientId, projectId, evidenceSourceSessionId}}
  );
  return result.data;
};

export const uploadChunks = (
  clientId: string,
  projectId: string,
  uploadSessionId: string,
  chunkIndex: number,
  checksum: string,
  chunk: Blob,
  evidenceSourceSessionId: string,
  signal?: AbortSignal
): Promise<AxiosResponse<UploadSessionResponse>> => {
  return apiClient.post(
    evidenceSourceEndpoint(
      `/https/uploads/chunks?evidenceSourceSessionId=${evidenceSourceSessionId}&clientId=${clientId}&projectId=${projectId}&uploadSessionId=${uploadSessionId}&chunkIndex=${chunkIndex}`
    ),
    chunk,
    {
      signal: signal,
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Checksum': checksum,
      },
    }
  );
};

export const completeUploadSession = async (
  clientId: string,
  projectId: string,
  uploadSessionId: string,
  evidenceSourceSessionId: string,
  signal?: AbortSignal
) => {
  const result = await apiClient.post(
    evidenceSourceEndpoint(
      `/https/uploads/complete?evidenceSourceSessionId=${evidenceSourceSessionId}&clientId=${clientId}&projectId=${projectId}&uploadSessionId=${uploadSessionId}`
    ),
    {signal: signal}
  );
  return result.data;
};

export const getUploadedChunksBySessionId = async (
  uploadSessionId: string,
  signal?: AbortSignal
) => {
  const result = await apiClient.get(
    evidenceSourceEndpoint(`/${uploadSessionId}/status`),
    {signal: signal}
  );
  return result.data;
};

export const cancelUploadSession = async (
  clientId: string,
  projectId: string,
  uploadSessionId: string,
  signal?: AbortSignal
) => {
  return apiClient.post(
    evidenceSourceEndpoint(
      `/https/upload/cancel?clientId=${clientId}&projectId=${projectId}&uploadSessionId=${uploadSessionId}`
    ),
    null,
    {signal}
  );
};

export const deleteUploadedFile = async (
  clientId: string,
  projectId: string,
  uploadSessionId: string
) => {
  return apiClient.delete(
    evidenceSourceEndpoint(
      `/upload/delete?clientId=${clientId}&projectId=${projectId}&uploadSessionId=${uploadSessionId}`
    )
  );
};

export const initiateProcessing = (
  clientId: string,
  projectId: string,
  payload: InitiateProcessingPayload
): Promise<AxiosResponse<InitiateProcessingResponse>> => {
  return apiClient.post(
    evidenceSourceEndpoint(
      `/staged/initiateIntake?clientId=${clientId}&projectId=${projectId}`
    ),
    payload
  );
};

export const getProcessingSettings = (
  clientId: string,
  projectId: string
): Promise<AxiosResponse<ProcessingSettingsResponse>> => {
  return apiClient.get(
    evidenceSourceEndpoint(
      `/processingsetting?clientId=${clientId}&projectId=${projectId}`
    )
  );
};

export const initiateIntakeUpload = (
  clientId: string,
  projectId: string,
  evidenceSourceSessionId: string,
  payload: InitiateUploadPayload
): Promise<AxiosResponse<InitiateProcessingResponse>> => {
  return apiClient.post(
    evidenceSourceEndpoint(
      `/initiateIntake?evidenceSourceSessionId=${evidenceSourceSessionId}&clientId=${clientId}&projectId=${projectId}`
    ),
    payload
  );
};

export const assignCustodian = (
  clientId: string,
  projectId: string,
  payload: AssignCustodianPayload
): Promise<AxiosResponse<InitiateProcessingResponse>> => {
  return apiClient.post(
    evidenceSourceEndpoint(
      `/staged/assigncustodian?clientId=${clientId}&projectId=${projectId}`
    ),
    payload
  );
};

export const checkEvidenceDuplicateName = async (
  clientId: string,
  projectId: string,
  evidenceName: string
) => {
  const result = await apiClient.get(
    evidenceSourceEndpoint(
      `/checkforduplicatename?clientId=${clientId}&projectId=${projectId}&evidenceName=${evidenceName}`
    )
  );
  return result.data;
};

export const getEvidenceIntakeProcessStatus = (params: {
  clientId: string;
  projectId: string;
  safeId: string;
}): Promise<AxiosResponse> => {
  return apiClient.get(evidenceSourceEndpoint('/intakeStatus'), {
    params: params,
  });
};

export const getFCEncryptedPayload = (): Promise<AxiosResponse> => {
  return apiClient.get(evidenceSourceEndpoint(`/filecatalyst/connectionKey`));
};

export const getEvidenceSourceSessionId = async () => {
  const result = await apiClient.get(evidenceSourceEndpoint('/sessionid'));
  return result.data;
};

export const getFileCatalyst = async (
  signal: AbortSignal,
  onDownloadProgress?: (progressEvent: any) => void
) => {
  const result = await apiClient.get(
    evidenceSourceEndpoint('/filecatalyst/Installer'),
    {
      responseType: 'blob',
      signal,
      onDownloadProgress,
    }
  );
  return result;
};